import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import EmailEditor from 'components/EmailEditor';
import APModal from 'components/Modal/APModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  sendEmail,
  getEmailStatus,
  deleteEmailStatus,
  getEmailTemplates,
  getEmailThreads,
  getCachedEmailThreads,
} from 'api/email';
import { getLead } from 'api/leads';
import { getSingleLead as getSingleLeadAction } from 'containers/Leads/actions';
import { setIsForbidden } from 'containers/App/actions';
import { useAction } from 'hooks/useAction';
import Avatar from 'components/Avatar';
import { LandingContent } from 'components/LandingContent';
import { Loading } from 'components/Loading';
import leadLandingImage from 'assets/images/leads-landing.png';
import Threads from './Threads';

const EmptyThreads = ({ handleDisplayToggle }) => {
  const description =
    `Keep tabs on your client and lead related email conversations
    right here in your email center. Create a new email to get started.`;
  return (
    <div className="threads__empty-container">
      <LandingContent
        action={() => handleDisplayToggle(false)}
        title="Welcome to your Email Center."
        description={description}
        actionButtonTitle="Create new email"
        heroImage={leadLandingImage}
      />
    </div>
  );
};

export default function LeadsEmailer() {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrations, integrationsEmails: userEmail = [] } = userInfo;
  const leads = useSelector(state => state.leads?.leads?.items || {});
  const leadsById = useSelector(state => state.leads?.leadsById);

  const { orgId, leadId, mode } = useParams();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const returnUrl = searchParams.get('returnUrl');
  const arg = searchParams.get('arg');

  const getSingleLead = useAction(() => getSingleLeadAction({ id: parseInt(leadId, 10) }));
  const currentLead = Object.keys(leadsById).length > 0 && leadsById[leadId] ? leadsById[leadId] : null;
  const { contactFirstName, contactLastName, contactName, contactEmail } = currentLead || {};
  const avatarImages = importAll(require.context('../../assets/images/leads/nature', false, /\.svg/));
  // #TODO: New webpack build exports the 5 images twice, so need to use a magic number to get
  // avatar images to work correctly until the webpack build can be inspected.
  const avatarImageId = leadId % 5 + 1;
  const avatarName =  `${contactFirstName?.charAt(0)}${contactLastName?.charAt(0)}`.toUpperCase();

  const [displayThreads, setDisplayThreads] = useState(true);
  const [emailFetchFinished, setEmailFetchFinished] = useState(false);
  const [activeThreads, setActiveThreads] = useState([]);
  const [archivedThreads, setArchivedThreads] = useState([]);
  const [replyMessage, setReplyMessage] = useState({});
  const [loading, setLoading] = useState(true);
  const [emailState, setEmailState] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [recipient, setRecipient] = useState();
  const [preview, setPreview] = useState(false);
  const [userGrantId, setUserGrantId] = useState();
  const [selectedThread, setSelectedThread] = useState({});
  const [messagesLoading, setMessagesLoading] = useState({});
  const mostRecentlySentThreadId = useRef(null);
  let timer;

  // If all the integrations for the current user are disconnected, we don't want them to be able to reply.
  const allDisconnected = integrations.every(integration => integration.grantStatus === 'disconnected');
  const readOnlyMode = (userInfo?.permissions['lead.view'] && userEmail.length === 0) || allDisconnected;

  const isReplying = Object.keys(replyMessage).length;

  const _setIsForbidden = useAction(setIsForbidden);

  const formatRecipients = (recipients) => {
    return recipients
      ? recipients.map((r) => ({ email: r }))
      : [];
  };

  const formatAttachments = (attachments) => {
    return attachments.map(attachment => {
      return {
        ...attachment,
        content: attachment.content.split(',')[1],
      };
    });
  };

  const fetchEmailStatus = async (id) => {
    try {
      const response = await getEmailStatus(id);

      if (response.ok) {
        const { status } = response.data;
        if (status === 'sent' || status === 'error') {
          clearInterval(timer);
          await deleteEmailStatus(id);
        }
        return status;
      };
      return 'error';
    } catch {
      return 'error';
    }
  };

  const handleEmailSend = async (payload) => {
    try {
      const message = {
        to: formatRecipients(payload.recipients),
        cc: formatRecipients(payload.cc),
        bcc: formatRecipients(payload.bcc),
        reply_to: formatRecipients(payload.replyTo),
        subject: payload.subject,
        body: payload.message,
        attachments: formatAttachments(payload.attachments),
      };

      if (payload.sender) {
        message.from = formatRecipients([payload.sender]);
      }

      if (payload.replyId) {
        message.reply_to_message_id = payload.replyId;
      }

      const response = await sendEmail({ lead_id: leadId, message });
      if (response.ok && response.data.id) {
        return new Promise((resolve) => {
          timer = setInterval(async (mostRecentThreadId) => {
            const status = await fetchEmailStatus(response.data.id);
            if (status === 'sent' || status === 'error') {
              // Only refresh when the most recently sent thread is sent
              const targetThreadId = payload.threadId || selectedThread.id;
              if (targetThreadId === mostRecentThreadId.current) {
                await refreshThreads();
              }
              // Send message to angular app to close the modal.
              window.parent.postMessage(`email-${status}`);
              resolve(status);
            }
          }, 1000, mostRecentlySentThreadId);
        });
      }
      return 'error';
    } catch {
      return 'error';
    }
  };

  const modalSendEmail = async (payload) => {
    let completed = false;
    setEmailState('sending');

    // If we are replying to a thread, we only need to add to the messages
    // Otherwise we stub in a new thread with a temporary ID
    const tempId = `temp-${Date.now()}`;
    const tempMsgId = `temp-${Date.now()}-msg`;
    payload.threadId = tempId;
    if (!payload.replyId) {
      mostRecentlySentThreadId.current = tempId;
      setActiveThreads((prevThreads) => {
        prevThreads.push({
          id: tempId,
          subject: payload.subject,
          lead_id: leadId,
          latest_message_received_date: Date.now() / 1000,
          thread_archived: false,
          message_ids: [tempMsgId],
          temporaryMessages: { [tempMsgId]: { body: payload.message, date: Date.now() / 1000 } },
        });
        return [...prevThreads.sort((a, b) => {
          return b.latest_message_received_date - a.latest_message_received_date;
        })];
      });
    } else {
      payload.threadId = selectedThread.id;
      mostRecentlySentThreadId.current = selectedThread.id;
      setActiveThreads((prevThreads) => {
        const updatedThreads = prevThreads.map(thread => {
          if (thread.id === selectedThread.id) {
            thread.message_ids.push(tempMsgId);
            thread.latest_message_received_date = Date.now() / 1000;
            thread.temporaryMessages = {
              ...thread.temporaryMessages,
              [tempMsgId]: { body: payload.message, date: Date.now() / 1000 },
            };
          }
          return thread;
        });
        return [...updatedThreads];
      });
    }


    const timeoutId = setTimeout(() => {
      if (!completed) {
        setEmailState('');
        setDisplayThreads(true);
      }
    }, 1500);

    const result = await handleEmailSend(payload);

    completed = true;
    clearTimeout(timeoutId);
    setEmailState(result);
    if (result === 'error') return;

    // Reset reply message state after sending email with no error.
    setEmailState('');

    // We don't want to refresh threads if this component
    // will redirect back to previous context.
    if (returnUrl) {
      return;
    }

    if (selectedThread.id) {
      setMessagesLoading({ ...messagesLoading, [selectedThread.id]: true });
    }
  };

  const splitThreads = (threads) => {
    return threads.reduce((acc, thread) => {
      if (thread.thread_archived) {
        acc[1].push(thread);
      } else {
        acc[0].push(thread);
      }
      return acc;
    }, [[], []]);
  };

  const loadCounter = useRef(0);

  const fetchEmailThreads = async ({ sortArchived = false } = {}) => {
    // #TODO: Remove loadCounter and replace it with a more reliable mechanism
    loadCounter.current += 1;
    const currentLoad = loadCounter.current;
    let page = 1;
    const perPage = 10;
    let hasMore = true;
    let isFirstLoad = true;
    let retried = false;
    const params = { per_page: perPage };
    if (sortArchived) params.sort_archived = sortArchived;
    if (window.location.hash) params.all_threads = true;

    async function threadSubroutine(threads) {
      if (threads.value.status === 403) {
        _setIsForbidden();
        return;
      }

      if (threads.value.ok) {
        const { user_grant_id, threads: fetchedThreads } = threads.value.data;
        if (user_grant_id) {
          setUserGrantId(user_grant_id);
        }

        const [newActiveThreads, newArchivedThreads] = splitThreads(fetchedThreads);

        // Check that new threads are not duplicates of existing threads.
        const activeThreadsUpdated =
          JSON.stringify(newActiveThreads.map(t => t.id)) !== JSON.stringify(activeThreads.map(t => t.id));
        const archivedThreadsUpdated =
          JSON.stringify(newArchivedThreads.map(t => t.id)) !== JSON.stringify(archivedThreads.map(t => t.id));

        if (!activeThreadsUpdated && !archivedThreadsUpdated) {
          return;
        }

        const newActiveThreadsSubjects = newActiveThreads.map(t => t.subject);
        if (isFirstLoad) {
          setActiveThreads(newActiveThreads.sort((a, b) =>
            b.latest_message_received_date - a.latest_message_received_date));
          setArchivedThreads(newArchivedThreads.sort((a, b) =>
            b.latest_message_received_date - a.latest_message_received_date));
          isFirstLoad = false;
        } else {
          const newActiveThreadIds = newActiveThreads.map(t => t.id);
          const newArchivedThreadIds = newArchivedThreads.map(t => t.id);

          setActiveThreads(prev => {
            const filteredPrev = prev.filter(t => {
              if (t.id.startsWith('temp-')) {
                return !newActiveThreadsSubjects.includes(t.subject);
              }
              return !newActiveThreadIds.includes(t.id) && !newActiveThreadsSubjects.includes(t.subject);
            });

            const finalThreads = [
              ...filteredPrev,
              ...newActiveThreads,
            ].sort((a, b) => b.latest_message_received_date - a.latest_message_received_date);

            return finalThreads;
          });

          const filteredArchivedThreads = [
            ...archivedThreads.filter(t => !newArchivedThreadIds.includes(t.id)),
            ...newArchivedThreads,
          ].sort((a, b) => b.latest_message_received_date - a.latest_message_received_date);
          setArchivedThreads(filteredArchivedThreads);
        }
      }
    }

    async function fetchNextThreads() {
      if (!hasMore || loadCounter.current !== currentLoad) return;

      const cachedThreads = await getCachedEmailThreads(orgId, leadId);

      if (!cachedThreads.length && !retried) {
        // Wait a couple of seconds before fetching again.
        // We could be fetching too fast before the cache is updated.
        setTimeout(() => {
          if (loadCounter.current === currentLoad) {
            fetchNextThreads();
          }
        }, 2000);
        retried = true;
        return;
      }

      // Since this is just the cache, we may have temporary threads that are not in the database.
      // We just need to merge them with the fetched cached threads, but immediately remove temps that match real ones.
      const cachedSubjects = cachedThreads.value.data.threads.map(t => t.subject);
      // Determine which cached threads are not stale by latest message received date.
      const nonStaleCachedThreads = cachedThreads.value.data.threads.filter(t => {
        return !activeThreads
          .some(at => at.id === t.id && at.latest_message_received_date < t.latest_message_received_date);
      });
      const activeAndUpdatedThreads = activeThreads.filter(t => {
        return t.latest_message_received_date >=
          (cachedThreads.value.data.threads.find(ct => ct.id === t.id)?.latest_message_received_date || 0) ||
          t.id.startsWith('temp-') && !cachedSubjects.includes(t.subject);
      });
      const cachedAndTempThreads = [
        ...nonStaleCachedThreads,
        ...activeAndUpdatedThreads,
      ];
      cachedThreads.value.data.threads = cachedAndTempThreads;

      threadSubroutine(cachedThreads);
      setLoading(false);

      params.page = page;
      getEmailThreads(orgId, leadId, params).then((threads) => {

        if (threads.value.ok && threads.value.data.threads.length) {
          const threadSubjects = threads.value.data.threads.map(t => t.subject);
          // Filter out temp threads whose subjects are present in the fetched threads.
          const tempThreads = activeThreads.filter(t => {
            return t.id.startsWith('temp-') && !threadSubjects.includes(t.subject);
          });
          threads.value.data.threads = [
            ...threads.value.data.threads,
            ...tempThreads,
          ];

          threadSubroutine(threads);
        }

        setLoading(false);

        if (threads.value.ok) {
          hasMore = threads.length === perPage;
          page += 1;

          if (hasMore) {
            setTimeout(() => {
              if (loadCounter.current === currentLoad) {
                fetchNextThreads();
              }
            }, 100);
          }
        }
      });
    }

    await fetchNextThreads();
  };

  const refreshThreads = async ({ sortArchived } = {}) => {
    await fetchEmailThreads({ sortArchived });
    setDisplayThreads(true);
  };

  const handleDisplayToggle = (val) => {
    if (val !== undefined) {
      setDisplayThreads(val);
      return;
    }
    setDisplayThreads(!displayThreads);
  };

  const handleDiscard = () => {
    if (returnUrl) {
      history.push(returnUrl);
      return;
    }
    if (mode === 'frame') {
      // Send message to angular app to close the modal.
      window.parent.postMessage('email-discarded');
    }
    // If not being redirected, we want to display threads after discarding email.
    handleDisplayToggle(true);
  };

  const handleStartNewThread = () => {
    setReplyMessage({});
    handleDisplayToggle(false);
  };

  const handleBack = () => {
    if (!displayThreads) {
      setDisplayThreads(true);
    } else {
      history.goBack();
    }
  };

  const errorModalCloseHandler = () => {
    setEmailState('');
  };

  const handleEmailEditorClose = () => {
    if (preview) {
      setPreview(false);
      return;
    }

    if (returnUrl) {
      history.push(returnUrl);
      return;
    }
    setDisplayThreads(true);
  };

  useEffect(() => {
    async function fetchEmailData() {
      await fetchEmailThreads();
      const [templates, _mergeFields] = await getEmailTemplates(orgId, leadId);
      if (templates.ok) {
        setEmailTemplates(templates.data);
      }
      if (_mergeFields.ok) {
        setMergeFields(_mergeFields.data);
      }
      setEmailFetchFinished(true);
    }

    fetchEmailData();

    async function fetchLeadData() {
      getSingleLead();
      const response = await getLead(orgId, leadId);

      if (response.ok && response.data.contact.emails.length) {
        setRecipient(response.data.contact.emails[0].field_value);
      }
    };

    if (Object.keys(leads).length) {
      // #TODO: Probably don't need this anymore since reducing the leads payload
      // into a leadsByKey object. We can just check that by key of id.
      const filteredLead = leads.filter(lead => lead.id === Number(leadId));
      setRecipient(filteredLead[0].contactEmail);
    } else {
      fetchLeadData();
    }
  }, []);

  useEffect(() => {
    if (arg === 'new') {
      handleStartNewThread();
    }
  }, [activeThreads, archivedThreads]);

  if (loading) {
    return (
      <Loading />
    );
  }

  if (mode === 'frame') {
    return (
      <EmailEditor
        title='Create a new email.'
        senderEmail={userEmail}
        recipients={recipient ? [recipient] : []}
        allowCopies
        allowBccSender={false}
        onSubmit={modalSendEmail}
        status={emailState}
        replyMessage={replyMessage}
        onDiscard={handleDiscard}
        threads={activeThreads}
        dataLoaded={emailFetchFinished}
        templates={emailTemplates}
        mergeFields={mergeFields}
        allowFormatting
        handlePreviewToggle={setPreview}
        preview={preview}
      />
    );
  }

  return (
    <div className="container lead-emailer">
      { !preview && (
        <div className="lead-emailer__header">
          <div className="lead-emailer__actions page-title">
            <div className="page-back">
              <button
                className="back-button"
                onClick={handleBack}
              >
                <FontAwesomeIcon icon={['fa', 'arrow-left']} />
                Back
              </button>
            </div>
            <div className="page-title__actions">
              {(!readOnlyMode && displayThreads) && (
                <button
                  type="button"
                  className="button button--md button--action no-collapse"
                  onClick={handleStartNewThread}
                >
                  Create new email
                </button>
              )}
            </div>
          </div>
          {displayThreads && (
            <div className="lead-emailer__contact">
              <Avatar
                photo={avatarImages[`nature-${avatarImageId}.svg`]}
                text={avatarName}
                size={85}
                borderSize={5}
                className="avatar--transparent avatar--dark"
              />
              <div className="lead-contact-item">
                <p className="lead-contact-item__name">
                  {contactName}
                </p>
                <p
                  className="lead-contact-item__email"
                >
                  {contactEmail}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      {
        [...activeThreads, ...archivedThreads].length === 0 ?
          (
            <EmptyThreads handleDisplayToggle={handleDisplayToggle} />
          ) : (
            <Threads
              userEmail={{ name: `${userInfo.firstName} ${userInfo.lastName}`, email: userEmail[0] }}
              activeThreads={activeThreads}
              archivedThreads={archivedThreads}
              setReplyMessage={setReplyMessage}
              handleDisplayToggle={handleDisplayToggle}
              refreshThreads={refreshThreads}
              orgId={orgId}
              readOnlyMode={readOnlyMode}
              leadId={leadId}
              leadEmail={{ name: `${contactName}`, email: contactEmail }}
              userGrantId={userGrantId}
              selectedThread={selectedThread}
              setSelectedThread={setSelectedThread}
              messagesLoading={messagesLoading}
              setMessagesLoading={setMessagesLoading}
            />
          )
      }
      <APModal
        name="email-editor"
        open={!displayThreads}
        onClose={handleEmailEditorClose}
        shouldCloseOnOverlayClick={false}
      >
        <>
          {isReplying ? (
            <EmailEditor
              senderEmail={userEmail}
              recipients={[replyMessage.from[0].email]}
              allowCopies
              allowBccSender={false}
              onSubmit={modalSendEmail}
              status={emailState}
              replyMessage={replyMessage}
              onDiscard={handleDiscard}
              threads={activeThreads}
              dataLoaded={emailFetchFinished}
              templates={emailTemplates}
              mergeFields={mergeFields}
              allowFormatting
              cc={replyMessage.cc.map(a => a.email).join(', ')}
              bcc={replyMessage.bcc.map(a => a.email).join(', ')}
              subject={replyMessage.subject}
              disableRecipient
              handlePreviewToggle={setPreview}
              preview={preview}
              scrollToOnMount
            />
          ) :
          (
            <EmailEditor
              title='Create a new email.'
              senderEmail={userEmail}
              recipients={recipient ? [recipient] : []}
              allowCopies
              allowBccSender={false}
              onSubmit={modalSendEmail}
              status={emailState}
              replyMessage={replyMessage}
              onDiscard={handleDiscard}
              threads={activeThreads}
              dataLoaded={emailFetchFinished}
              templates={emailTemplates}
              mergeFields={mergeFields}
              allowFormatting
              handlePreviewToggle={setPreview}
              preview={preview}
            />
          )}
        </>
      </APModal>
      <APModal
        name="error-send"
        open={emailState === 'error'}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Got it
          </button>
        )}
      >
        <div>Sorry, we couldn't send email, please try again.</div>
      </APModal>
    </div>
  );
};

function importAll(r) {
  const images = {};
  r.keys().forEach(item => {
    images[item.replace('./', '')] = r(item);
  });
  return images;
}
